import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Briefcase, Plus, Search } from 'lucide-react';
import { jobsAPI } from '../../utils/api';

const JobManagement = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [jobs, setJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchJobs();
  }, []);

  useEffect(() => {
    filterJobs();
  }, [jobs, searchTerm, statusFilter]);

  const fetchJobs = async () => {
    try {
      const response = await jobsAPI.list();
      setJobs(response.data);
    } catch (error) {
      toast.error(t('control_panel.job_management.failed_to_load'));
    } finally {
      setLoading(false);
    }
  };

  const filterJobs = () => {
    let filtered = jobs;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(job =>
        job.job_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.from_address.city.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.to_address.city.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(job => job.status === statusFilter);
    }

    setFilteredJobs(filtered);
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-700',
      pending_estimate: 'bg-yellow-100 text-yellow-700',
      estimate_sent: 'bg-blue-100 text-blue-700',
      accepted: 'bg-green-100 text-green-700',
      scheduled: 'bg-purple-100 text-purple-700',
      in_progress: 'bg-orange-100 text-orange-700',
      completed: 'bg-green-100 text-green-700',
      cancelled: 'bg-red-100 text-red-700',
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="space-y-6" data-testid="job-management">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold mb-2">{t('control_panel.job_management.title')}</h1>
          <p className="text-gray-600">{t('control_panel.job_management.subtitle')}</p>
        </div>
        <Button onClick={() => navigate('/control-panel/jobs/create')} data-testid="create-job-btn">
          <Plus size={20} className="mr-2" />
          {t('control_panel.job_management.create_new')}
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <Input
                placeholder={t('control_panel.job_management.search_placeholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder={t('control_panel.job_management.filter_by_status')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('control_panel.job_management.all_statuses')}</SelectItem>
                <SelectItem value="draft">{t('control_panel.job_management.statuses.draft')}</SelectItem>
                <SelectItem value="pending_estimate">{t('control_panel.job_management.statuses.pending_estimate')}</SelectItem>
                <SelectItem value="estimate_sent">{t('control_panel.job_management.statuses.estimate_sent')}</SelectItem>
                <SelectItem value="accepted">{t('control_panel.job_management.statuses.accepted')}</SelectItem>
                <SelectItem value="scheduled">{t('control_panel.job_management.statuses.scheduled')}</SelectItem>
                <SelectItem value="in_progress">{t('control_panel.job_management.statuses.in_progress')}</SelectItem>
                <SelectItem value="completed">{t('control_panel.job_management.statuses.completed')}</SelectItem>
                <SelectItem value="cancelled">{t('control_panel.job_management.statuses.cancelled')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Jobs List */}
      <Card>
        <CardHeader>
          <CardTitle>{t('jobs')} ({filteredJobs.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">{t('common.loading')}</div>
          ) : filteredJobs.length === 0 ? (
            <div className="text-center py-12">
              <Briefcase className="mx-auto text-gray-400 mb-4" size={48} />
              <p className="text-gray-600 mb-4">{t('control_panel.job_management.no_jobs_found')}</p>
              <Button onClick={() => navigate('/control-panel/jobs/create')}>
                <Plus size={16} className="mr-2" />
                {t('create_first_job')}
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredJobs.map((job) => (
                <div
                  key={job.id}
                  className="p-4 border rounded-lg hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => navigate(`/control-panel/jobs/${job.id}`)}
                  data-testid={`job-${job.id}`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-lg">{job.job_number}</h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(job.status)}`}>
                          {t(`control_panel.job_management.statuses.${job.status}`) || job.status.replace('_', ' ')}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-1">
                        <span className="font-medium">{t('from_address')}:</span> {job.from_address.street}, {job.from_address.city}
                      </p>
                      <p className="text-sm text-gray-600 mb-2">
                        <span className="font-medium">{t('to_address')}:</span> {job.to_address.street}, {job.to_address.city}
                      </p>
                      <div className="flex gap-4 text-xs text-gray-500">
                        <span>{job.customer_type}</span>
                        {job.move_out_date && (
                          <span>{t('move_date')}: {new Date(job.move_out_date).toLocaleDateString()}</span>
                        )}
                      </div>
                    </div>
                    <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); navigate(`/control-panel/jobs/${job.id}`); }}>
                      {t('view_details')}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default JobManagement;
